import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Upload, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { createApplication } from "@/lib/apiClient";
import MonoVideo from "@/components/MonoVideo";

const EXPERIENCE = [
  { value: "none", label: "Complete beginner" },
  { value: "some", label: "Some shoots / freelance" },
  { value: "professional", label: "Professional / signed before" },
];

// Compress an image file to a small base64 data URL.
const fileToCompressedDataUrl = (file, maxDim = 900, quality = 0.72) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDim) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else if (height > maxDim) {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const emptyForm = {
  full_name: "", email: "", phone: "", age: "", city: "", country: "",
  height_cm: "", measurements: "", experience: "", instagram: "", tiktok: "",
  portfolio_url: "", goals: "",
};

export default function Apply({ sidebarImage, sidebarVideo }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = 4 - photos.length;
    if (room <= 0) {
      toast.error("Maximum of 4 photos.");
      return;
    }
    try {
      const selected = files.slice(0, room);
      const encoded = await Promise.all(selected.map((f) => fileToCompressedDataUrl(f)));
      setPhotos((p) => [...p, ...encoded]);
    } catch {
      toast.error("Could not read one of the images.");
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const validateStep = () => {
    if (step === 0) {
      if (!form.full_name.trim()) return "Please enter your full name.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email.";
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(2, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    const err = validateStep();
    if (err) { toast.error(err); setStep(0); return; }
    setSubmitting(true);
    try {
      await createApplication({
        ...form,
        age: form.age ? parseInt(form.age, 10) : null,
        photos,
      });
      setDone(true);
      toast.success("Application received. We'll be in touch.");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setForm(emptyForm); setPhotos([]); setStep(0); setDone(false);
  };

  return (
    <section
      id="apply"
      data-testid="apply-section"
      className="bg-[#0a0a0a] text-[#ffffff] grid grid-cols-1 lg:grid-cols-2"
    >
      {/* Sticky editorial image */}
      <div className="relative hidden lg:block">
        <div className="sticky top-0 h-screen overflow-hidden">
          {sidebarVideo ? (
            <MonoVideo
              src={sidebarVideo}
              alt="Get scouted"
              testId="apply-video"
              className="h-full w-full"
              videoClassName="h-full w-full object-cover"
            />
          ) : (
            <img src={sidebarImage} alt="Get scouted" className="h-full w-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-[#0a0a0a]/40 pointer-events-none" />
          <div className="absolute bottom-12 left-10 right-10 pointer-events-none">
            <p className="font-script text-[#ffffff] text-5xl xl:text-7xl leading-none mb-3">
              go on —
            </p>
            <h2 className="font-display text-5xl xl:text-7xl font-black uppercase tracking-tighter leading-[0.85]">
              Your <span className="text-[#ffffff]">turn</span> on the wall.
            </h2>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="px-6 md:px-16 py-24 flex flex-col justify-center min-h-screen">
        <div className="max-w-xl w-full mx-auto">
          <div className="flex items-center gap-4 mb-3">
            <span className="h-px w-12 bg-[#ffffff]" />
            <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#ffffff]/60">
              Get Scouted
            </span>
          </div>
          <p className="font-script text-[#ffffff] text-4xl md:text-5xl leading-none mb-10">
            make them remember your name.
          </p>

          {done ? (
            <motion.div
              data-testid="apply-success"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="py-10"
            >
              <div className="w-16 h-16 rounded-full bg-[#ffffff] flex items-center justify-center mb-8">
                <Check size={30} strokeWidth={2} className="text-black" />
              </div>
              <h3 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-tight mb-6">
                You&rsquo;re on our radar.
              </h3>
              <p className="text-[#ffffff]/70 leading-relaxed mb-10">
                Thanks, {form.full_name.split(" ")[0] || "superstar"}. Our scouting team reviews every
                application personally. If there&rsquo;s a fit, you&rsquo;ll hear from us soon.
              </p>
              <button
                data-testid="apply-reset-btn"
                onClick={resetAll}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#ffffff]/70 hover:text-[#ffffff] transition-colors"
              >
                Submit another application
              </button>
            </motion.div>
          ) : (
            <>
              <h3 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-none mb-4">
                Step {step + 1}<span className="text-[#ffffff]">/</span>3
              </h3>
              <div className="flex gap-2 mb-12">
                {[0, 1, 2].map((s) => (
                  <span
                    key={s}
                    className={`h-1 flex-1 transition-colors duration-500 ${
                      s <= step ? "bg-[#ffffff]" : "bg-white/15"
                    }`}
                  />
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.35, ease: "easeInOut" }}
                  className="space-y-8"
                >
                  {step === 0 && (
                    <>
                      <Field label="Full Name *">
                        <input data-testid="input-full-name" className="vv-input" value={form.full_name} onChange={set("full_name")} placeholder="e.g. Ava Bennett" />
                      </Field>
                      <Field label="Email *">
                        <input data-testid="input-email" type="email" className="vv-input" value={form.email} onChange={set("email")} placeholder="you@email.com" />
                      </Field>
                      <div className="grid grid-cols-2 gap-6">
                        <Field label="Phone">
                          <input data-testid="input-phone" className="vv-input" value={form.phone} onChange={set("phone")} placeholder="+1 ..." />
                        </Field>
                        <Field label="Age">
                          <input data-testid="input-age" type="number" className="vv-input" value={form.age} onChange={set("age")} placeholder="18" />
                        </Field>
                      </div>
                      <div className="grid grid-cols-2 gap-6">
                        <Field label="City">
                          <input data-testid="input-city" className="vv-input" value={form.city} onChange={set("city")} placeholder="Paris" />
                        </Field>
                        <Field label="Country">
                          <input data-testid="input-country" className="vv-input" value={form.country} onChange={set("country")} placeholder="France" />
                        </Field>
                      </div>
                    </>
                  )}

                  {step === 1 && (
                    <>
                      <div className="grid grid-cols-2 gap-6">
                        <Field label="Height (cm)">
                          <input data-testid="input-height" className="vv-input" value={form.height_cm} onChange={set("height_cm")} placeholder="178" />
                        </Field>
                        <Field label="Measurements">
                          <input data-testid="input-measurements" className="vv-input" value={form.measurements} onChange={set("measurements")} placeholder="86-61-89" />
                        </Field>
                      </div>
                      <Field label="Experience">
                        <Select value={form.experience} onValueChange={(v) => setForm((f) => ({ ...f, experience: v }))}>
                          <SelectTrigger data-testid="select-experience" className="vv-input rounded-none border-0 border-b border-white/30 focus:ring-0 px-0 text-[#ffffff]">
                            <SelectValue placeholder="Select your level" />
                          </SelectTrigger>
                          <SelectContent>
                            {EXPERIENCE.map((o) => (
                              <SelectItem key={o.value} value={o.value} data-testid={`experience-${o.value}`}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <div className="grid grid-cols-2 gap-6">
                        <Field label="Instagram">
                          <input data-testid="input-instagram" className="vv-input" value={form.instagram} onChange={set("instagram")} placeholder="@handle" />
                        </Field>
                        <Field label="TikTok">
                          <input data-testid="input-tiktok" className="vv-input" value={form.tiktok} onChange={set("tiktok")} placeholder="@handle" />
                        </Field>
                      </div>
                      <Field label="Portfolio / Website">
                        <input data-testid="input-portfolio" className="vv-input" value={form.portfolio_url} onChange={set("portfolio_url")} placeholder="https://..." />
                      </Field>
                      <Field label="What do you want from your career?">
                        <textarea data-testid="input-goals" className="vv-input resize-none" rows={3} value={form.goals} onChange={set("goals")} placeholder="Tell us your dream..." />
                      </Field>
                    </>
                  )}

                  {step === 2 && (
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#ffffff]/60 block mb-4">
                        Photos (up to 4)
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                        {photos.map((p, i) => (
                          <div key={i} className="relative group aspect-[3/4] overflow-hidden">
                            <img src={p} alt={`upload ${i}`} className="w-full h-full object-cover" />
                            <button
                              data-testid={`remove-photo-${i}`}
                              onClick={() => removePhoto(i)}
                              className="absolute top-1 right-1 bg-black/70 hover:bg-[#000000] p-1 transition-colors"
                              aria-label="Remove"
                            >
                              <X size={14} className="text-white" />
                            </button>
                          </div>
                        ))}
                        {photos.length < 4 && (
                          <button
                            data-testid="upload-photo-btn"
                            onClick={() => fileRef.current?.click()}
                            className="aspect-[3/4] border border-dashed border-white/30 hover:border-[#ffffff] flex flex-col items-center justify-center gap-2 text-[#ffffff]/50 hover:text-[#ffffff] transition-colors"
                          >
                            <Upload size={22} strokeWidth={1.5} />
                            <span className="text-[10px] uppercase tracking-[0.2em]">Add</span>
                          </button>
                        )}
                      </div>
                      <input ref={fileRef} data-testid="file-input" type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" />
                      <p className="text-xs text-[#ffffff]/40 leading-relaxed">
                        A clean face shot and a full-body shot work best. No filters, natural light.
                        Photos are optional but boost your chances.
                      </p>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="flex items-center justify-between mt-12">
                {step > 0 ? (
                  <button
                    data-testid="form-back-btn"
                    onClick={back}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#ffffff]/60 hover:text-[#ffffff] transition-colors"
                  >
                    <ArrowLeft size={16} /> Back
                  </button>
                ) : <span />}

                {step < 2 ? (
                  <button
                    data-testid="form-next-btn"
                    onClick={next}
                    className="group inline-flex items-center gap-3 rounded-full bg-[#ffffff] text-[#0a0a0a] hover:bg-[#000000] hover:text-white px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold transition-colors duration-300"
                  >
                    Continue
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </button>
                ) : (
                  <button
                    data-testid="form-submit-btn"
                    onClick={submit}
                    disabled={submitting}
                    className="group inline-flex items-center gap-3 rounded-full bg-[#000000] text-white hover:bg-[#1f1f1f] px-10 py-4 text-xs uppercase tracking-[0.2em] font-bold transition-colors duration-300 disabled:opacity-60"
                  >
                    {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending</> : <>Submit Application <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></>}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#ffffff]/60 block mb-2">
      {label}
    </label>
    {children}
  </div>
);
